/** Map ISO-4217 (or symbol) commerce settings to a display symbol for public shop UI. */

const CODE_TO_SYMBOL: Record<string, string> = {
  eur: '€',
  usd: '$',
  gbp: '£',
  jpy: '¥',
  cny: '¥',
  chf: 'CHF',
  cad: 'C$',
  aud: 'A$',
  sek: 'kr',
  nok: 'kr',
  dkk: 'kr',
  pln: 'zł',
  czk: 'Kč',
  huf: 'Ft',
  ron: 'lei',
  try: '₺',
  brl: 'R$',
  mxn: 'MX$',
  inr: '₹',
};

/** Shop-level tax hint beside the price (default none so existing shops stay unchanged). */
export type PriceTaxDisplayMode = 'none' | 'incl' | 'excl';

/** Per-product override; inherit uses the shop default. */
export type ProductPriceTaxDisplay = 'inherit' | PriceTaxDisplayMode;

/** Explicit preference or locale-derived auto. */
export type CurrencyPositionPref = 'auto' | 'left' | 'right';

export type ResolvedCurrencyPosition = 'left' | 'right';

export type FormatMoneyOptions = {
  /** BCP-47 / site language — drives number format and auto currency position. */
  locale?: string | null;
  /** Shop preference; default `left` when omitted (legacy callers). */
  currencyPosition?: CurrencyPositionPref | null;
};

/**
 * Locales that conventionally place the currency symbol after the amount.
 * English (en, en-*) → left; most European + common others → right.
 */
const CURRENCY_RIGHT_LOCALES = new Set([
  'de', 'fr', 'es', 'it', 'nl', 'pt', 'pl', 'cs', 'sk', 'hu', 'ro',
  'fi', 'sv', 'no', 'nb', 'nn', 'da', 'el', 'hr', 'sl', 'bg', 'lt', 'lv', 'et',
  'uk', 'ru', 'tr', 'ca', 'eu', 'gl', 'is', 'ga', 'mt', 'sq', 'sr', 'bs', 'mk',
  'be', 'az', 'kk', 'uz',
]);

/** Stable cart line id for collection-backed products (grid + PDP share the same id). */
export function collectionProductCartId(collectionSlug: string, itemSlug: string): string {
  return `col:${collectionSlug}:${itemSlug}`;
}

export function commerceCurrencySymbol(raw?: string | null): string {
  const v = String(raw || '').trim();
  if (!v) return '€';
  if (/^[€$£¥]$/.test(v) || v.length <= 3 && /[€$£¥]/.test(v)) return v;
  const code = v.toLowerCase().replace(/[^a-z]/g, '');
  if (CODE_TO_SYMBOL[code]) return CODE_TO_SYMBOL[code];
  if (v.length <= 4) return v;
  return '€';
}

export function parseCommercePrice(raw: string): number {
  const cleaned = raw.replace(/[^0-9.,]/g, '').replace(',', '.');
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : 0;
}

export function normalizePriceTaxDisplay(raw: unknown): PriceTaxDisplayMode {
  const v = String(raw ?? '').trim().toLowerCase();
  if (v === 'incl' || v === 'excl' || v === 'none') return v;
  return 'none';
}

export function normalizeProductPriceTaxDisplay(raw: unknown): ProductPriceTaxDisplay {
  const v = String(raw ?? '').trim().toLowerCase();
  if (v === 'incl' || v === 'excl' || v === 'none' || v === 'inherit') return v;
  return 'inherit';
}

export function normalizeCurrencyPositionPref(raw: unknown): CurrencyPositionPref {
  const v = String(raw ?? '').trim().toLowerCase();
  if (v === 'left' || v === 'right' || v === 'auto') return v;
  return 'auto';
}

/** Resolve effective tax hint mode (product override wins when not inherit). */
export function resolvePriceTaxDisplay(
  shopDefault: unknown,
  productOverride?: unknown,
): PriceTaxDisplayMode {
  const product = normalizeProductPriceTaxDisplay(productOverride);
  if (product !== 'inherit') return product;
  return normalizePriceTaxDisplay(shopDefault);
}

/**
 * Map locale → currency symbol side.
 * English → left (for $, £, etc.); European locales → right.
 * Euro (€) in `auto` mode is always right — European convention (`12,99 €`),
 * including when the UI/price locale is English.
 */
export function resolveCurrencyPosition(
  preference: CurrencyPositionPref | null | undefined,
  locale?: string | null,
  currency?: string | null,
): ResolvedCurrencyPosition {
  const pref = normalizeCurrencyPositionPref(preference ?? 'auto');
  if (pref === 'left' || pref === 'right') return pref;
  const sym = String(currency || '').trim()
    ? commerceCurrencySymbol(currency)
    : '';
  // Euro: European shops place the glyph after the amount in every locale.
  if (sym === '€') return 'right';
  const base = String(locale || '')
    .trim()
    .toLowerCase()
    .split(/[-_]/)[0];
  if (!base) return 'right';
  if (base === 'en') return 'left';
  if (CURRENCY_RIGHT_LOCALES.has(base)) return 'right';
  return 'right';
}

/** Locale-aware amount (e.g. DE `39,99` / EN `39.99`). Falls back to fixed 2 decimals. */
export function formatLocaleAmount(amount: number, locale?: string | null): string {
  const v = Math.round(amount * 100) / 100;
  const loc = String(locale || '').trim();
  if (!loc) return v.toFixed(2);
  try {
    return new Intl.NumberFormat(loc, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(v);
  } catch {
    return v.toFixed(2);
  }
}

/**
 * Format money for display.
 * Without `options`, keeps legacy `€12.34` (symbol left, no space, `.` decimals).
 * With `options`, applies locale number format + currency position (incl. `auto`).
 */
export function formatCommerceMoney(
  amount: number,
  currency = '€',
  options?: FormatMoneyOptions,
): string {
  const v = Math.round(amount * 100) / 100;
  if (!options) {
    return `${currency}${v.toFixed(2)}`;
  }
  const num = formatLocaleAmount(v, options.locale);
  const pos = resolveCurrencyPosition(options.currencyPosition, options.locale, currency);
  // NBSP between amount and symbol so `39,99 €` / `€ 39.99` never wrap mid-price.
  if (pos === 'right') return `${num}\u00a0${currency}`;
  return `${currency}\u00a0${num}`;
}

/**
 * Prefer an already-formatted price string; otherwise format the numeric amount.
 * When `options` is provided (locale / currency position), always reformat parseable
 * amounts — even if the raw string already includes a currency glyph (e.g. stored
 * `€25.99` must become `25,99 €` for DE auto position).
 */
export function displayCommercePrice(
  raw: string,
  currency = '€',
  options?: FormatMoneyOptions,
): string {
  const t = String(raw || '').trim();
  if (!t) return '';
  const n = parseCommercePrice(t);
  if (n > 0) {
    if (options) return formatCommerceMoney(n, currency, options);
    // Legacy (no options): keep author-formatted strings that already include a glyph.
    if (/[€$£¥]/.test(t)) return t;
    return formatCommerceMoney(n, currency);
  }
  return t;
}

/**
 * Prefer `site.priceTaxIncl` / `site.priceTaxExcl` via i18n in UI.
 */
export function priceTaxDisplayLabel(
  mode: PriceTaxDisplayMode,
  locale?: string | null,
): string {
  if (mode === 'none') return '';
  const base = String(locale || 'en')
    .trim()
    .toLowerCase()
    .split(/[-_]/)[0];
  if (base === 'de') {
    return mode === 'incl' ? 'inkl. MwSt.' : 'exkl. MwSt.';
  }
  return mode === 'incl' ? 'incl. Tax' : 'excl. Tax';
}

/** Where listed-price tax hints appear. Default product_page. */
export type PriceTaxDisplayScope =
  | 'everywhere'
  | 'product_page'
  | 'product_grid'
  | 'product_page_and_grid';

export type PriceTaxSurface = 'product_page' | 'product_grid' | 'checkout' | 'other';

export function normalizePriceTaxDisplayScope(raw: unknown): PriceTaxDisplayScope {
  const v = String(raw ?? '').trim().toLowerCase();
  if (
    v === 'everywhere'
    || v === 'product_page'
    || v === 'product_grid'
    || v === 'product_page_and_grid'
  ) {
    return v;
  }
  return 'product_page';
}

/** Whether the tax hint should render on a given storefront surface. */
export function shouldShowPriceTaxLabel(
  scope: PriceTaxDisplayScope | null | undefined,
  surface: PriceTaxSurface,
): boolean {
  const s = normalizePriceTaxDisplayScope(scope);
  if (s === 'everywhere') return true;
  if (s === 'product_page') return surface === 'product_page';
  if (s === 'product_grid') return surface === 'product_grid';
  return surface === 'product_page' || surface === 'product_grid';
}

/** Default ISO currency code from project language (DE → eur, else usd). */
export function defaultCommerceCurrencyCode(
  settings?: { language?: string } | null,
): 'eur' | 'usd' {
  const lang = String(settings?.language || '').toLowerCase();
  if (lang === 'de' || lang.startsWith('de-')) return 'eur';
  return 'usd';
}

/** Resolve display symbol from project settings (DE sites default to € when currency is unset/usd). */
export function resolveProjectCurrencySymbol(
  settings?: { commerce?: { currency?: string }; language?: string } | null,
): string {
  const raw = settings?.commerce?.currency;
  const lang = String(settings?.language || '').toLowerCase();
  if ((!raw || String(raw).toLowerCase() === 'usd') && (lang === 'de' || lang.startsWith('de-'))) {
    return '€';
  }
  return commerceCurrencySymbol(raw);
}
